/**
 * Collection "buy in place" cards (Clôture parts).
 *
 * Handles the quantity stepper, the variant select (price refresh) and the
 * add-to-cart submit for product cards rendered with the
 * `product-card-buy` snippet on the collection page.
 *
 * All listeners are delegated to `document` on purpose: the collection
 * section is swapped via AJAX (filters / sorting / pagination) and card
 * markup is re-injected with `innerHTML`, so element-bound listeners would
 * be lost.
 *
 * Depends on:
 * - cart-functions.js (window.addToCart, window.updateCartCount)
 * - helper-functions.js (window.formatMoney)
 * - the add-to-cart-popup snippet (window.showAddToCartPopup)
 */
(function () {
  if (window.__collectionBuyCardsInitialized) return;
  window.__collectionBuyCardsInitialized = true;

  function money(cents) {
    if (typeof window.formatMoney === 'function') return window.formatMoney(cents);
    return (Number(cents) / 100).toFixed(2) + '$';
  }

  function getQuantityInput(qtyWrapper) {
    return qtyWrapper ? qtyWrapper.querySelector('input[name="quantity"]') : null;
  }

  function clampQuantity(input, delta) {
    var min = Number(input.min || 1) || 1;
    var current = Number(input.value || min) || min;
    var next = current + delta;
    if (next < min) next = min;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // --- Quantity stepper -----------------------------------------------------
  document.addEventListener('click', function (event) {
    var minus = event.target.closest('[data-qty-minus]');
    var plus = event.target.closest('[data-qty-plus]');
    if (!minus && !plus) return;

    var qtyWrapper = (minus || plus).closest('[data-card-qty]');
    var input = getQuantityInput(qtyWrapper);
    if (!input) return;

    event.preventDefault();
    clampQuantity(input, minus ? -1 : 1);
  });

  // --- Variant select: refresh the card price -------------------------------
  document.addEventListener('change', function (event) {
    var select = event.target.closest('[data-card-variant-select]');
    if (!select) return;

    var option = select.options[select.selectedIndex];
    if (!option || !option.value) return;

    var li = select.closest('li');
    if (!li) return;

    // Toggle variant-dependent feature bullets (e.g. the « couvre base »
    // bullet only applies to the « avec base » variants of the poteau).
    li.querySelectorAll('[data-feature-variants]').forEach(function (item) {
      var applicableIds = item.getAttribute('data-feature-variants') || '';
      item.hidden = applicableIds.indexOf(',' + option.value + ',') === -1;
    });

    var priceEl = li.querySelector('._info ._price');
    if (!priceEl) return;

    var price = Number(option.getAttribute('data-price') || 0);
    var comparePrice = Number(option.getAttribute('data-compare-price') || 0);

    if (!price) return;

    if (comparePrice > price) {
      priceEl.classList.add('_price--discounted');
      priceEl.innerHTML =
        '<span data-product-price-current>' + money(price) + '</span> ' +
        '<s data-product-price-compare>' + money(comparePrice) + '</s>';
    } else {
      priceEl.classList.remove('_price--discounted');
      priceEl.innerHTML = '<span data-product-price-current>' + money(price) + '</span>';
    }

    // Keep client-side price sorting coherent with the selected variant.
    if (li) li.setAttribute('data-sort-price', String(price));
  });

  // --- Initial sync ----------------------------------------------------------
  // Browsers can restore a previous <select> value on reload/back-forward
  // without firing `change`; re-dispatch it so price + feature bullets match.
  function syncRestoredSelects() {
    document.querySelectorAll('[data-card-variant-select]').forEach(function (select) {
      if (select.value) {
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      // Slight delay: form restoration can happen after DOMContentLoaded.
      setTimeout(syncRestoredSelects, 0);
    });
  } else {
    setTimeout(syncRestoredSelects, 0);
  }
  window.addEventListener('pageshow', function () {
    setTimeout(syncRestoredSelects, 0);
  });

  // --- Add to cart -----------------------------------------------------------
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-card-buy-form]');
    if (!form) return;

    // Without the AJAX helper, fall back to the native /cart/add submit.
    if (typeof window.addToCart !== 'function') return;

    event.preventDefault();

    var formData = new FormData(form);
    var variantId = formData.get('id');
    var quantity = Number(formData.get('quantity') || 1) || 1;
    if (!variantId) {
      // Should not happen (the select is `required`), extra safety net.
      if (typeof form.reportValidity === 'function') form.reportValidity();
      return;
    }

    var submitButton = form.querySelector('[data-card-add-to-cart]');
    if (submitButton && submitButton.disabled) return;
    if (submitButton) submitButton.disabled = true;

    // Selected variant info (price / title) for the confirmation popup.
    var variantTitle = '';
    var unitPrice = null;
    var select = form.querySelector('[data-card-variant-select]');
    if (select) {
      var option = select.options[select.selectedIndex];
      if (option && option.value) {
        variantTitle = option.getAttribute('data-variant-title') || '';
        unitPrice = Number(option.getAttribute('data-price') || 0) || null;
      }
    } else {
      var hiddenId = form.querySelector('input[name="id"]');
      if (hiddenId) unitPrice = Number(hiddenId.getAttribute('data-price') || 0) || null;
    }

    window
      .addToCart({
        id: variantId,
        quantity: quantity > 0 ? quantity : 1,
      })
      .then(function () {
        var popup = document.querySelector('[data-add-to-cart-popup]');
        if (popup) {
          var imageEl = popup.querySelector('[data-popup-image]');
          var titleEl = popup.querySelector('[data-popup-title]');
          var variantTitleEl = popup.querySelector('[data-popup-variant-title]');
          var imageSrc = form.getAttribute('data-product-image') || '';

          if (imageEl) {
            if (imageSrc) {
              imageEl.src = imageSrc;
              imageEl.alt = form.getAttribute('data-product-title') || '';
              imageEl.hidden = false;
              imageEl.style.display = '';
            } else {
              imageEl.hidden = true;
            }
          }
          if (titleEl) titleEl.textContent = form.getAttribute('data-product-title') || '';
          if (variantTitleEl) variantTitleEl.textContent = variantTitle;
        }

        if (typeof window.showAddToCartPopup === 'function') {
          window.showAddToCartPopup({
            price: unitPrice != null ? money(unitPrice) : undefined,
          });
        }
        if (typeof window.updateCartCount === 'function') {
          window.updateCartCount();
        }
      })
      .catch(function (error) {
        console.error('Error adding product to cart:', error);
        // Native fallback so the customer is never blocked.
        if (submitButton) submitButton.disabled = false;
        form.submit();
      })
      .then(function () {
        if (submitButton) submitButton.disabled = false;
      });
  });
})();
